import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { mockApiInterceptor } from './core/interceptors/mock-api.interceptor';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNoopAnimations } from '@angular/platform-browser/animations';

export const themePreset = definePreset(Lara, {
  name: 'lara',
  semantic: {
    primary: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },
  },
});

const commonInterceptors = [authInterceptor, errorInterceptor];
const interceptors = environment.useMockData
  ? [mockApiInterceptor, ...commonInterceptors]
  : commonInterceptors;

export interface AppConfigOptions {
  useNoopAnimations?: boolean;
}

export const appConfig = (options: AppConfigOptions = {}): ApplicationConfig => ({
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withNavigationErrorHandler((e) => {
        const isChunkError =
          e.error instanceof TypeError &&
          (e.error.message.includes('Failed to fetch dynamically imported module') ||
            e.error.message.includes('Importing a module script failed'));
        if (isChunkError) {
          window.location.reload();
        }
      })
    ),
    provideHttpClient(withInterceptors(interceptors)),
    MessageService,
    options.useNoopAnimations ? provideNoopAnimations() : provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: themePreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
      inputVariant: 'filled',
    }),
  ],
});
